import type { ResumeExperience } from 'types/resume';
import styles from './ExperienceCard.module.scss';

interface Props {
  role: ResumeExperience;
}

function formatDate(d: string): string {
  if (!d) return '';
  if (d === 'present') return 'Present';
  const [year, month] = d.split('-');
  if (!month) return year;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

export default function ExperienceCard({ role }: Props) {
  const dateRange = `${formatDate(role.start)} – ${formatDate(role.end)}`;
  const isCurrent = role.end === 'present';
  const hasDetails = Boolean(role.summary)
    || role.achievements.length > 0
    || role.responsibilities.length > 0
    || role.technologies.length > 0;

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.dot} ${isCurrent ? styles.dotCurrent : ''}`} />

      <div className={styles.card}>
        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <div className={styles.roleTitle}>{role.title}</div>
            <div className={styles.company}>{role.organization}</div>
            <div className={styles.meta}>
              <span className={styles.metaItem}>{dateRange}</span>
              {role.location && <span className={styles.metaItem}>{role.location}</span>}
            </div>
          </div>
        </div>

        {hasDetails && (
          <div className={styles.expanded}>
            {role.summary && (
              <p className={styles.summary}>{role.summary}</p>
            )}

            {role.achievements.length > 0 && (
              <div className={styles.section}>
                <ul className={styles.bulletList}>
                  {role.achievements.map((a, i) => (
                    <li key={i} className={styles.bulletItem}>
                      <strong className={styles.bulletTitle}>{a.title}:</strong> {a.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {role.responsibilities.length > 0 && (
              <div className={styles.section}>
                <h4 className={styles.sectionLabel}>Responsibilities</h4>
                <ul className={styles.bulletList}>
                  {role.responsibilities.map((r, i) => (
                    <li key={i} className={styles.bulletItem}>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {role.technologies.length > 0 && (
              <div className={styles.section}>
                <h4 className={styles.sectionLabel}>Technology Stack</h4>
                <div className={styles.techPills}>
                  {role.technologies.map((t, i) => (
                    <span key={i} className={styles.techPill}>{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
